import { Select, SelectItem } from '@carbon/react';
import { asProblem, ListTable, PageHeader, problemMessage, StatusTag, useFormat } from '@pos/admin-ui';
import { useTranslation } from '@pos/i18n';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import {
  listSubmissions,
  STATUS_TAG,
  SUBMISSION_STATUSES,
  submissionsKey,
  type FormSubmissionItem,
  type SubmissionStatus,
} from './formSubmissionsApi';

/**
 * What visitors sent through the website's forms (CMS phase 2), newest first, by state; a row opens the answers. The list shows
 * the form and the first answer only.
 */
export function FormSubmissionsPage() {
  const { t } = useTranslation();
  const { dateTime } = useFormat();
  const navigate = useNavigate();
  const [status, setStatus] = useState('new');
  const [paging, setPaging] = useState({ page: 1, pageSize: 25 });
  const submissions = useQuery({
    queryKey: submissionsKey(status, paging.page, paging.pageSize),
    queryFn: () => listSubmissions(status, paging.page, paging.pageSize),
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
  });

  return (
    <section className="pos-admin-page">
      <PageHeader title={t('formSubmissions.title')} description={t('formSubmissions.intro')} />
      <ListTable<FormSubmissionItem>
        ariaLabel={t('formSubmissions.title')}
        loading={submissions.isLoading}
        error={submissions.error ? problemMessage(t, asProblem(submissions.error)) : undefined}
        onRetry={() => void submissions.refetch()}
        rows={submissions.data?.items ?? []}
        toolbarActions={
          <Select
            id="form-submissions-status"
            labelText={t('formSubmissions.columns.status')}
            hideLabel
            inline
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPaging((p) => ({ ...p, page: 1 }));
            }}
          >
            <SelectItem value="" text={t('formSubmissions.allStatuses')} />
            {SUBMISSION_STATUSES.map((s) => (
              <SelectItem key={s} value={s} text={t(`formSubmissions.status.${s}`)} />
            ))}
          </Select>
        }
        onRowClick={(s) => void navigate(`/cms/submissions/${s.id}`)}
        rowLabel={(s) => s.formTitle ?? s.id}
        empty={{ heading: t('formSubmissions.empty') }}
        pagination={{ ...paging, total: Number(submissions.data?.total ?? 0), capped: submissions.data?.totalCapped, onChange: setPaging }}
        columns={[
          { key: 'formTitle', header: t('formSubmissions.columns.form'), render: (s) => s.formTitle ?? '—' },
          { key: 'preview', header: t('formSubmissions.columns.preview'), render: (s) => s.preview ?? '—' },
          { key: 'submittedAt', header: t('formSubmissions.columns.when'), render: (s) => dateTime(s.submittedAt) },
          {
            key: 'status',
            header: t('formSubmissions.columns.status'),
            render: (s) => (
              <StatusTag status={STATUS_TAG[s.status as SubmissionStatus] ?? 'inactive'}>
                {t(`formSubmissions.status.${s.status as SubmissionStatus}`)}
              </StatusTag>
            ),
          },
        ]}
      />
    </section>
  );
}
